
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * SymbolProfitLossMultipleInfo struct
 */
class SymbolProfitLossMultipleInfo {
  /**
   * Initialize struct
   * @param {!string=} symbolName
   * @param {!number=} symbolDayProfitLoss
   * @param {!number=} buyProfitLoss
   * @param {!number=} sellProfitLoss
   * @constructor
   */
  constructor (symbolName = '', symbolDayProfitLoss = 0.0, buyProfitLoss = 0.0, sellProfitLoss = 0.0) {
    this.SymbolName = symbolName
    this.SymbolDayProfitLoss = symbolDayProfitLoss
    this.BuyProfitLoss = buyProfitLoss
    this.SellProfitLoss = sellProfitLoss
  }

  /**
   * Copy struct (shallow copy)
   * @this {!SymbolProfitLossMultipleInfo}
   * @param {!SymbolProfitLossMultipleInfo} other Other struct
   * @returns {!SymbolProfitLossMultipleInfo} This struct
   */
  copy (other) {
    if (other.SymbolName != null) {
      this.SymbolName = other.SymbolName
    } else {
      this.SymbolName = null
    }
    if (other.SymbolDayProfitLoss != null) {
      this.SymbolDayProfitLoss = other.SymbolDayProfitLoss
    } else {
      this.SymbolDayProfitLoss = null
    }
    if (other.BuyProfitLoss != null) {
      this.BuyProfitLoss = other.BuyProfitLoss
    } else {
      this.BuyProfitLoss = null
    }
    if (other.SellProfitLoss != null) {
      this.SellProfitLoss = other.SellProfitLoss
    } else {
      this.SellProfitLoss = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!SymbolProfitLossMultipleInfo}
   * @returns {!SymbolProfitLossMultipleInfo} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new SymbolProfitLossMultipleInfoModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new SymbolProfitLossMultipleInfoModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!SymbolProfitLossMultipleInfo}
   * @param {!SymbolProfitLossMultipleInfo} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof SymbolProfitLossMultipleInfo)) {
      throw new TypeError('Instance of SymbolProfitLossMultipleInfo is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!SymbolProfitLossMultipleInfo}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      SymbolName: ((this.SymbolName != null) ? this.SymbolName : null),
      SymbolDayProfitLoss: ((this.SymbolDayProfitLoss != null) ? this.SymbolDayProfitLoss : null),
      BuyProfitLoss: ((this.BuyProfitLoss != null) ? this.BuyProfitLoss : null),
      SellProfitLoss: ((this.SellProfitLoss != null) ? this.SellProfitLoss : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return SymbolProfitLossMultipleInfo.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!SymbolProfitLossMultipleInfo} other Object value
   * @returns {!SymbolProfitLossMultipleInfo} Created struct
   */
  static fromObject (other) {
    return new SymbolProfitLossMultipleInfo().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!SymbolProfitLossMultipleInfo}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return SymbolProfitLossMultipleInfo.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!SymbolProfitLossMultipleInfo}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5224
  }
}

export { SymbolProfitLossMultipleInfo };

/**
 * Fast Binary Encoding SymbolProfitLossMultipleInfo field model
 */
class FieldModelSymbolProfitLossMultipleInfo extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.SymbolName = new fbe.FieldModelString(buffer, 4 + 4)
    this.SymbolDayProfitLoss = new fbe.FieldModelDouble(buffer, this.SymbolName.fbeOffset + this.SymbolName.fbeSize)
    this.BuyProfitLoss = new fbe.FieldModelDouble(buffer, this.SymbolDayProfitLoss.fbeOffset + this.SymbolDayProfitLoss.fbeSize)
    this.SellProfitLoss = new fbe.FieldModelDouble(buffer, this.BuyProfitLoss.fbeOffset + this.BuyProfitLoss.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelSymbolProfitLossMultipleInfo}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelSymbolProfitLossMultipleInfo}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.SymbolName.fbeSize + this.SymbolDayProfitLoss.fbeSize + this.BuyProfitLoss.fbeSize + this.SellProfitLoss.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelSymbolProfitLossMultipleInfo}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.SymbolName.fbeExtra + this.SymbolDayProfitLoss.fbeExtra + this.BuyProfitLoss.fbeExtra + this.SellProfitLoss.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelSymbolProfitLossMultipleInfo}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelSymbolProfitLossMultipleInfo.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelSymbolProfitLossMultipleInfo}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5224
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelSymbolProfitLossMultipleInfo}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelSymbolProfitLossMultipleInfo}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.SymbolName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SymbolName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolName.fbeSize

    if ((fbeCurrentSize + this.SymbolDayProfitLoss.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SymbolDayProfitLoss.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolDayProfitLoss.fbeSize

    if ((fbeCurrentSize + this.BuyProfitLoss.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.BuyProfitLoss.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.BuyProfitLoss.fbeSize

    if ((fbeCurrentSize + this.SellProfitLoss.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SellProfitLoss.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SellProfitLoss.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelSymbolProfitLossMultipleInfo}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelSymbolProfitLossMultipleInfo}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelSymbolProfitLossMultipleInfo}
   * @param {!SymbolProfitLossMultipleInfo} fbeValue Default value, defaults is new SymbolProfitLossMultipleInfo()
   * @returns {!SymbolProfitLossMultipleInfo} SymbolProfitLossMultipleInfo value
   */
  get (fbeValue = new SymbolProfitLossMultipleInfo()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelSymbolProfitLossMultipleInfo}
   * @param {!SymbolProfitLossMultipleInfo} fbeValue SymbolProfitLossMultipleInfo value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.SymbolName.fbeSize) <= fbeStructSize) {
      fbeValue.SymbolName = this.SymbolName.get()
    } else {
      fbeValue.SymbolName = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolName.fbeSize

    if ((fbeCurrentSize + this.SymbolDayProfitLoss.fbeSize) <= fbeStructSize) {
      fbeValue.SymbolDayProfitLoss = this.SymbolDayProfitLoss.get()
    } else {
      fbeValue.SymbolDayProfitLoss = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolDayProfitLoss.fbeSize

    if ((fbeCurrentSize + this.BuyProfitLoss.fbeSize) <= fbeStructSize) {
      fbeValue.BuyProfitLoss = this.BuyProfitLoss.get()
    } else {
      fbeValue.BuyProfitLoss = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.BuyProfitLoss.fbeSize

    if ((fbeCurrentSize + this.SellProfitLoss.fbeSize) <= fbeStructSize) {
      fbeValue.SellProfitLoss = this.SellProfitLoss.get()
    } else {
      fbeValue.SellProfitLoss = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SellProfitLoss.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelSymbolProfitLossMultipleInfo}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelSymbolProfitLossMultipleInfo}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelSymbolProfitLossMultipleInfo}
   * @param {!SymbolProfitLossMultipleInfo} fbeValue SymbolProfitLossMultipleInfo value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelSymbolProfitLossMultipleInfo}
   * @param {SymbolProfitLossMultipleInfo} fbeValue SymbolProfitLossMultipleInfo value
   */
  setFields (fbeValue) {
    this.SymbolName.set(fbeValue.SymbolName)
    this.SymbolDayProfitLoss.set(fbeValue.SymbolDayProfitLoss)
    this.BuyProfitLoss.set(fbeValue.BuyProfitLoss)
    this.SellProfitLoss.set(fbeValue.SellProfitLoss)
  }
}

export { FieldModelSymbolProfitLossMultipleInfo };

/**
 * Fast Binary Encoding SymbolProfitLossMultipleInfo model
 */
class SymbolProfitLossMultipleInfoModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelSymbolProfitLossMultipleInfo(this.buffer, 4)
  }

  /**
   * Get the SymbolProfitLossMultipleInfo model
   * @this {!SymbolProfitLossMultipleInfoModel}
   * @returns {!FieldModelSymbolProfitLossMultipleInfo} model SymbolProfitLossMultipleInfo model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!SymbolProfitLossMultipleInfoModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!SymbolProfitLossMultipleInfoModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return SymbolProfitLossMultipleInfoModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!SymbolProfitLossMultipleInfoModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelSymbolProfitLossMultipleInfo.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!SymbolProfitLossMultipleInfoModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!SymbolProfitLossMultipleInfoModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!SymbolProfitLossMultipleInfoModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!SymbolProfitLossMultipleInfoModel}
   * @param {!SymbolProfitLossMultipleInfo} value SymbolProfitLossMultipleInfo value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!SymbolProfitLossMultipleInfoModel}
   * @param {!SymbolProfitLossMultipleInfo} value SymbolProfitLossMultipleInfo value, defaults is new SymbolProfitLossMultipleInfo()
   * @return {!object} Deserialized SymbolProfitLossMultipleInfo value and its size
   */
  deserialize (value = new SymbolProfitLossMultipleInfo()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new SymbolProfitLossMultipleInfo(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new SymbolProfitLossMultipleInfo(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!SymbolProfitLossMultipleInfoModel}
   * @param {!number} prev Previous SymbolProfitLossMultipleInfo model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { SymbolProfitLossMultipleInfoModel };
